import { Injectable, signal, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { CurrentUser } from '../models/identity.model';
import { UserRole, IdentityService } from '@libs/core/api';
import { TokenRefreshService } from './token-refresh.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentIdentityService {
  private identityService = inject(IdentityService);
  private keycloak = inject(KeycloakService);
  private tokenRefreshService = inject(TokenRefreshService);

  // Current user signal (reactive state)
  currentUser = signal<CurrentUser | null>(null);
  isAuthenticated = signal(false);

  /**
   * Get current authenticated user from API
   */
  getCurrentUser(): Observable<CurrentUser> {
    return this.identityService.getCurrentUser().pipe(
      map((apiUser) => ({
        id: apiUser.id ?? '',
        email: apiUser.email ?? '',
        fullName: apiUser.fullName ?? '',
        role: this.mapRoleStringToEnum(apiUser.role ?? 'Member'),
        roleName: this.mapRoleStringToEnum(apiUser.role ?? 'Member'),
        teamId: apiUser.teamId ?? undefined,
        teamName: apiUser.teamName ?? undefined,
        isActive: apiUser.isActive ?? false,
      })),
    );
  }

  /**
   * Map role string from backend to UserRole enum
   */
  private mapRoleStringToEnum(roleString: string): UserRole {
    console.log('Role string:', roleString);
    switch (roleString) {
      case 'Admin':
        return UserRole.Admin;
      case 'TechnicalLeader':
        return UserRole.TechnicalLeader;
      case 'CodeQualityResponsible':
        return UserRole.CodeQualityResponsible;
      case 'Member':
        return UserRole.Member;
      default:
        return UserRole.Member;
    }
  }

  /**
   * Load and set current user
   * Call this after Keycloak login to load user data from backend
   */
  loadCurrentUser(): void {
    // Check if user is logged in via Keycloak
    if (!this.keycloak.isLoggedIn()) {
      this.currentUser.set(null);
      this.isAuthenticated.set(false);
      this.tokenRefreshService.stopAutoRefresh();
      return;
    }

    this.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
        // Start automatic token refresh after successful login
        this.tokenRefreshService.startAutoRefresh();
      },
      error: (error) => {
        console.error('Failed to load current user:', error);
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
        this.tokenRefreshService.stopAutoRefresh();
      },
    });
  }

  /**
   * Check if current user is Admin
   */
  isAdmin(): boolean {
    return this.currentUser()?.role === UserRole.Admin;
  }

  /**
   * Check if current user has a team (all roles except Admin)
   */
  hasTeam(): boolean {
    return this.currentUser()?.teamId != null;
  }

  /**
   * Check if current user is Team Member
   */
  isMember(): boolean {
    return this.currentUser()?.role === UserRole.Member;
  }

  /**
   * Check if current user is Technical Leader
   */
  isTechnicalLeader(): boolean {
    return this.currentUser()?.role === UserRole.TechnicalLeader;
  }

  /**
   * Check if current user is Code Quality Responsible
   */
  isCodeQualityResponsible(): boolean {
    return this.currentUser()?.role === UserRole.CodeQualityResponsible;
  }

  /**
   * Get current user's team ID (if Team Member)
   */
  getCurrentTeamId(): string | undefined {
    return this.currentUser()?.teamId;
  }

  /**
   * Logout from Keycloak and clear user data
   * Redirects to Keycloak logout page which then redirects back to app
   */
  async logout(): Promise<void> {
    // Stop automatic token refresh
    this.tokenRefreshService.stopAutoRefresh();

    this.currentUser.set(null);
    this.isAuthenticated.set(false);

    // Redirect to Keycloak logout (will redirect back to app after logout)
    await this.keycloak.logout(window.location.origin);
  }

  /**
   * Login via Keycloak
   * Redirects to Keycloak login page
   */
  async login(): Promise<void> {
    await this.keycloak.login({
      redirectUri: window.location.origin,
    });
  }

  /**
   * Get Keycloak user profile
   */
  getKeycloakProfile() {
    return this.keycloak.getKeycloakInstance().profile;
  }

  /**
   * Get Keycloak token
   */
  getKeycloakToken(): string | undefined {
    return this.keycloak.getKeycloakInstance().token;
  }

  // ===== Permission Checks =====

  /**
   * Can view Dashboard
   * All roles can view dashboard (Admin: all projects, others: team only)
   */
  canViewDashboard(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Can view Reports (Weekly/Monthly)
   * Admin, TL, CodeQualityResponsible
   */
  canViewReports(): boolean {
    const role = this.currentUser()?.role;
    return role === UserRole.Admin || role === UserRole.TechnicalLeader || role === UserRole.CodeQualityResponsible;
  }

  /**
   * Can view Projects list and project details
   * Admin: all projects
   * TechnicalLeader, CodeQualityResponsible: team projects
   */
  canViewProjects(): boolean {
    const role = this.currentUser()?.role;
    return role === UserRole.Admin || role === UserRole.TechnicalLeader || role === UserRole.CodeQualityResponsible;
  }

  /**
   * Can view Quality Gates
   * Admin, TechnicalLeader, CodeQualityResponsible
   */
  canViewQualityGates(): boolean {
    const role = this.currentUser()?.role;
    return role === UserRole.Admin || role === UserRole.TechnicalLeader || role === UserRole.CodeQualityResponsible;
  }

  /**
   * Can view Users list
   * Admin only (TL can manage team members via Team Settings)
   */
  canViewUsers(): boolean {
    return this.isAdmin();
  }

  /**
   * Can view Teams list
   * Admin only
   */
  canViewTeams(): boolean {
    return this.isAdmin();
  }

  /**
   * Can view Team Settings
   * TechnicalLeader only
   */
  canViewTeamSettings(): boolean {
    return this.isTechnicalLeader();
  }

  /**
   * Can manage own team (add/remove members, assign projects)
   * TechnicalLeader only
   */
  canManageTeam(): boolean {
    return this.isTechnicalLeader();
  }

  /**
   * Can import/sync data (projects, quality gates, history)
   * Admin and TechnicalLeader only
   */
  canImport(): boolean {
    const role = this.currentUser()?.role;
    return role === UserRole.Admin || role === UserRole.TechnicalLeader;
  }

  /**
   * Dashboard scope: returns 'all' for Admin, 'team' for others
   */
  getDashboardScope(): 'all' | 'team' {
    return this.isAdmin() ? 'all' : 'team';
  }
}
